/* ==========================================================================
 * Fit Finder — apparel size recommendation, ported from the Hydrogen storefront
 * (app/lib/fit.ts + FitFinder.tsx). Pure client-side arithmetic against the
 * product's own size chart; the profile is kept in localStorage. No backend,
 * no AI, no external vendor — honest chart matching.
 * ======================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'fashorio:fitprofile';
  var PROFILE_EVENT = 'fashorio:fitprofile';

  /* ---- constants (verbatim from lib/fit.ts) ---- */
  var TAPE_LIMITS = { bust: [24, 70], waist: [18, 70], hip: [24, 80] };
  var CUP_OFFSET = { AA: 0.5, A: 1, B: 2, C: 3, D: 4, DD: 5, DDD: 6, G: 7 };
  var WHR = { straighter: 0.83, average: 0.75, wider: 0.7 };
  var PREF_SHIFT = { 'much-tighter': -1.2, tighter: -0.6, average: 0, looser: 0.6, 'much-looser': 1.2 };
  var SIZE_LABEL = /^(xxs|xs|s|m|l|xl|xxl|xxxl|[0-5]?xl|\d{1,2}|s\/m|m\/l|l\/xl|xl\/xxl|one size)$/i;

  /* ---- standard BODY size chart (from /pages/sizing) ----
     The per-product description table is GARMENT (flat) measurement, which
     over-recommends by ~1 size when matched as if it were body measurements
     (a garment bust of 38.6" reads as "roomy" against a 34" body, so it sizes
     up). The recommendation therefore matches the shopper's body against this
     curated body chart, then maps the result onto the product's own size labels.
     Body ranges in inches. */
  var STANDARD_CHART = [
    { std: 'XS',  us: [0, 2],   bust: [32, 33],     waist: [24, 25],     hip: [34, 35] },
    { std: 'S',   us: [4, 6],   bust: [34, 35],     waist: [26, 27],     hip: [36, 37] },
    { std: 'M',   us: [8, 10],  bust: [36, 37.5],   waist: [28, 29.5],   hip: [38, 39.5] },
    { std: 'L',   us: [12, 14], bust: [38.5, 40],   waist: [30.5, 32],   hip: [40.5, 42] },
    { std: 'XL',  us: [16, 18], bust: [41.5, 43],   waist: [33.5, 35],   hip: [43.5, 45] },
    { std: 'XXL', us: [20, 22], bust: [44.5, 46.5], waist: [36.5, 38.5], hip: [46.5, 48.5] },
    // Plus extension (curated, continuing the ~3in per-size progression) so the
    // tool stays on for 3XL/3X..5XL/5X products instead of hiding.
    { std: '3XL', us: [24, 26], bust: [47.5, 49.5], waist: [39.5, 41.5], hip: [49.5, 51.5] },
    { std: '4XL', us: [28, 30], bust: [50.5, 52.5], waist: [42.5, 44.5], hip: [52.5, 54.5] },
    { std: '5XL', us: [32, 34], bust: [53.5, 55.5], waist: [45.5, 47.5], hip: [55.5, 57.5] }
  ];
  function byStd(std) { for (var i = 0; i < STANDARD_CHART.length; i++) { if (STANDARD_CHART[i].std === std) return STANDARD_CHART[i]; } return null; }

  // Map a product size label to a standard chart row, or null when it cannot be
  // mapped safely (3XL and up, S/M combos, one size, numeric outside 0..22).
  var ALPHA_MAP = {
    xs: 'XS', extrasmall: 'XS', s: 'S', small: 'S', m: 'M', medium: 'M', l: 'L', large: 'L',
    xl: 'XL', '1xl': 'XL', '1x': 'XL', extralarge: 'XL',
    xxl: 'XXL', '2xl': 'XXL', '2x': 'XXL',
    xxxl: '3XL', '3xl': '3XL', '3x': '3XL',
    '4xl': '4XL', '4x': '4XL',
    '5xl': '5XL', '5x': '5XL'
  };
  function stdRowForLabel(label) {
    var t = String(label || '').trim().toLowerCase().replace(/\s+/g, '');
    if (ALPHA_MAP[t]) return byStd(ALPHA_MAP[t]);
    if (/^\d{1,2}$/.test(t)) {
      var n = parseInt(t, 10);
      for (var i = 0; i < STANDARD_CHART.length; i++) {
        if (n >= STANDARD_CHART[i].us[0] && n <= STANDARD_CHART[i].us[1]) return STANDARD_CHART[i];
      }
    }
    return null;
  }

  // Build recommendation rows from the product's size labels mapped onto the
  // standard body chart. Returns null (so the tool hides) unless EVERY label maps
  // and at least two do — no guessing on unusual size systems.
  function standardRowsForSizes(sizeValues) {
    var rows = [], seenStd = {}, allMapped = true;
    (sizeValues || []).forEach(function (label) {
      if (!label) return;
      var r = stdRowForLabel(label);
      if (!r) { allMapped = false; return; }
      if (seenStd[r.std]) return; // one body range per size
      seenStd[r.std] = true;
      rows.push({ label: label, bust: r.bust.slice(), waist: r.waist.slice(), hip: r.hip.slice() });
    });
    return (allMapped && rows.length >= 2) ? rows : null;
  }

  /* ---- size chart parsing ---- */
  function toRange(text) {
    if (!text) return null;
    var m = text.replace(/["″in]/gi, '').match(/(\d+(?:\.\d+)?)\s*(?:to|-|–|—)\s*(\d+(?:\.\d+)?)/);
    if (m) return [parseFloat(m[1]), parseFloat(m[2])];
    var s = text.replace(/["″in]/gi, '').match(/(\d+(?:\.\d+)?)/);
    if (s) { var v = parseFloat(s[1]); return [v - 1, v + 1]; }
    return null;
  }

  function colKind(head) {
    var h = (head || '').toLowerCase();
    if (/bust|chest/.test(h)) return 'bust';
    if (/waist/.test(h)) return 'waist';
    if (/\bhips?\b|seat/.test(h)) return 'hip';
    if (/inseam|inside leg/.test(h)) return 'inseam';
    return null;
  }

  // Parse an HTML string (the size section) into [{label,bust,waist,hip,inseamIn}]
  function parseSizeChart(html) {
    if (!html) return [];
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var rows = [];

    var table = wrap.querySelector('table');
    if (table) {
      var trs = Array.prototype.slice.call(table.querySelectorAll('tr'));
      if (trs.length < 2) return [];
      var headCells = Array.prototype.slice.call(trs[0].querySelectorAll('th,td')).map(function (c) { return c.textContent.trim(); });
      var map = headCells.map(colKind); // index -> kind
      for (var i = 1; i < trs.length; i++) {
        var cells = Array.prototype.slice.call(trs[i].querySelectorAll('th,td')).map(function (c) { return c.textContent.trim(); });
        if (!cells.length) continue;
        var row = { label: cells[0] };
        for (var c = 1; c < cells.length; c++) {
          var kind = map[c];
          if (!kind) continue;
          if (kind === 'inseam') { var r = toRange(cells[c]); if (r) row.inseamIn = (r[0] + r[1]) / 2; }
          else { var rg = toRange(cells[c]); if (rg) row[kind] = rg; }
        }
        if (row.label && (row.bust || row.waist || row.hip)) rows.push(row);
      }
      if (rows.length >= 2) return rows;
    }

    // Fallback: per-size text lines "S: bust 31 to 33, waist 24 to 26, hip 34 to 35"
    var text = wrap.textContent || '';
    text.split(/\n|;/).forEach(function (line) {
      var mm = line.split(':');
      if (mm.length < 2) return;
      var label = mm[0].trim();
      if (!SIZE_LABEL.test(label)) return;
      var rest = mm.slice(1).join(':').toLowerCase();
      var row = { label: label };
      var bust = rest.match(/(?<!under\s)bust[^0-9]*([0-9.]+(?:\s*(?:to|-|–)\s*[0-9.]+)?)/);
      var waist = rest.match(/waist[^0-9]*([0-9.]+(?:\s*(?:to|-|–)\s*[0-9.]+)?)/);
      var hip = rest.match(/hip[^0-9]*([0-9.]+(?:\s*(?:to|-|–)\s*[0-9.]+)?)/);
      if (bust) { var b = toRange(bust[1]); if (b) row.bust = b; }
      if (waist) { var w = toRange(waist[1]); if (w) row.waist = w; }
      if (hip) { var hp = toRange(hip[1]); if (hp) row.hip = hp; }
      if (row.bust || row.waist || row.hip) rows.push(row);
    });
    return rows;
  }

  function detectStretchIn(text) {
    if (!text) return 0;
    var t = text.toLowerCase();
    var m = t.match(/(\d+(?:\.\d+)?)\s*(?:%|percent)\s*(?:elastane|spandex|lycra)/);
    if (m) { var pct = parseFloat(m[1]); return pct >= 5 ? 1.5 : pct >= 2 ? 1 : 0.5; }
    if (/non-?stretch|\brigid\b|100\s*%\s*cotton/.test(t)) return 0;
    if (/\b(stretch|jegging|ponte|scuba|ribbed|jersey|knit|seamless)\b/.test(t)) return 1;
    return 0;
  }

  function detectChartKind(html) {
    var t = (html || '').toLowerCase();
    if (/product measurement/.test(t)) return 'garment';
    if (/\d+(?:\.\d+)?\s*(?:to|-|–)\s*\d/.test(t)) return 'body';
    return 'unknown';
  }

  /* ---- the engine ---- */
  function estimateBody(p) {
    var tape = {
      bust: isValidTape('bust', p.bustIn) ? p.bustIn : undefined,
      waist: isValidTape('waist', p.waistIn) ? p.waistIn : undefined,
      hip: isValidTape('hip', p.hipIn) ? p.hipIn : undefined
    };
    var bmi = p.weightKg / Math.pow(p.heightCm / 100, 2);
    var frame = 0.65 + 0.35 * (p.heightCm / 165);
    var waistCm = (74 + (bmi - 22) * 2.6) * frame;
    if (p.belly === 'flatter') waistCm -= 3.5;
    if (p.belly === 'curvier') waistCm += 3.5;
    if (p.age && p.age > 30) waistCm += Math.min(4, (p.age - 30) * 0.09);
    if (tape.waist !== undefined) waistCm = tape.waist * 2.54;

    var hipCm = waistCm / (WHR[p.hip] || 0.75);

    var bustIn, bustMeasured = false;
    if (tape.bust !== undefined) { bustIn = tape.bust; bustMeasured = true; }
    else if (p.braBand && p.braCup) { bustIn = p.braBand + (CUP_OFFSET[p.braCup] || 0); bustMeasured = true; }
    else { bustIn = (waistCm + 16) / 2.54; }

    var shift = PREF_SHIFT[p.pref] || 0;
    return {
      bustIn: bustIn + shift,
      waistIn: (tape.waist !== undefined ? tape.waist : waistCm / 2.54) + shift,
      hipIn: (tape.hip !== undefined ? tape.hip : hipCm / 2.54) + shift,
      inseamIn: (p.heightCm * 0.45) / 2.54,
      measured: { bust: bustMeasured, waist: tape.waist !== undefined, hip: tape.hip !== undefined }
    };
  }

  function isValidTape(kind, v) {
    if (v === undefined || v === null || isNaN(v)) return false;
    var lim = TAPE_LIMITS[kind];
    return v >= lim[0] && v <= lim[1];
  }

  function detail(measure, bodyIn, range, give) {
    if (!range) return null;
    give = give || 0;
    var lo = range[0], top = range[1] + give;
    if (bodyIn >= lo && bodyIn <= top) return { measure: measure, bodyIn: bodyIn, range: range, verdict: 'fits', distIn: 0 };
    if (bodyIn > top) return { measure: measure, bodyIn: bodyIn, range: range, verdict: 'snug', distIn: bodyIn - top };
    return { measure: measure, bodyIn: bodyIn, range: range, verdict: 'roomy', distIn: lo - bodyIn };
  }

  function recommendSize(rows, profile, opts) {
    opts = opts || {};
    if (!rows.length) return null;
    var body = estimateBody(profile);
    var give = opts.stretchIn || 0;

    var scored = rows.map(function (row) {
      var details = [
        detail('Bust', body.bustIn, row.bust, give),
        detail('Waist', body.waistIn, row.waist, give),
        detail('Hip', body.hipIn, row.hip, give)
      ].filter(function (d) { return d; });
      if (!details.length) return { label: row.label, score: 0, raw: -1e9, details: details };
      var snugs = details.filter(function (d) { return d.verdict === 'snug'; }).map(function (d) { return d.distIn; });
      var roomies = details.filter(function (d) { return d.verdict === 'roomy'; }).map(function (d) { return d.distIn; });
      var worstSnug = snugs.length ? Math.max.apply(null, snugs) : 0;
      var restSnug = snugs.reduce(function (t, v) { return t + v; }, 0) - worstSnug;
      var roomyAvg = roomies.length ? roomies.reduce(function (t, v) { return t + v; }, 0) / roomies.length : 0;
      var penalty = worstSnug * 18 + restSnug * 10 + roomyAvg * 7;
      // `raw` keeps the true (unclamped) ordering. The displayed `score` is
      // clamped to 8..98, but sorting must use `raw` — otherwise a body larger
      // than every size makes all scores floor to 8, the sort ties, and the
      // FIRST row (the smallest size) wins. That once recommended XS to a
      // plus-size shopper. Sorting on `raw` keeps the least-out-of-range size on
      // top (the largest when nothing fits, the smallest when the body is tiny).
      var raw = 97 - penalty;
      var score = Math.round(Math.max(8, Math.min(98, raw)));
      return { label: row.label, score: score, raw: raw, details: details };
    });

    var sorted = scored.slice().sort(function (a, b) { return b.raw - a.raw; });
    var best = sorted[0], runnerUp = sorted[1] || null;

    var bestRow = rows.filter(function (r) { return r.label === best.label; })[0];
    var length = null;
    if (bestRow && bestRow.inseamIn) {
      var diff = bestRow.inseamIn - body.inseamIn;
      length = { garmentIn: bestRow.inseamIn, bodyIn: Math.round(body.inseamIn * 10) / 10, verdict: diff > 1.5 ? 'long' : diff < -1.5 ? 'short' : 'regular' };
    }

    var betweenSizes = null;
    if (runnerUp && best.score - runnerUp.score <= 6) {
      var mid = function (label) {
        var r = rows.filter(function (x) { return x.label === label; })[0];
        var band = r && (r.waist || r.hip || r.bust);
        return band ? (band[0] + band[1]) / 2 : 0;
      };
      var larger, smaller;
      if (mid(best.label) >= mid(runnerUp.label)) { larger = best.label; smaller = runnerUp.label; }
      else { larger = runnerUp.label; smaller = best.label; }
      if (larger !== smaller) betweenSizes = { larger: larger, smaller: smaller };
    }

    return {
      best: best, runnerUp: runnerUp,
      bustIn: Math.round(body.bustIn * 10) / 10, waistIn: Math.round(body.waistIn * 10) / 10, hipIn: Math.round(body.hipIn * 10) / 10,
      measured: body.measured, chartKind: opts.chartKind || 'unknown', length: length, betweenSizes: betweenSizes
    };
  }

  /* ---- profile persistence ---- */
  function loadProfile() {
    try {
      var v = JSON.parse(localStorage.getItem(STORE_KEY));
      return v && v.v === 2 && v.heightCm > 0 && v.weightKg > 0 ? v : null;
    } catch (e) { return null; }
  }
  function saveProfile(p) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch (e) {}
    document.dispatchEvent(new CustomEvent(PROFILE_EVENT));
  }
  function clearProfile() {
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
    document.dispatchEvent(new CustomEvent(PROFILE_EVENT));
  }
  /* Add the shopper to Shopify's email marketing list via the storefront customer
     form (same path a newsletter block uses), tagged so we know it came from here. */
  /* Email capture uses a REAL server-rendered {% form 'customer' %} (in
     snippets/fit-finder.liquid), submitted by a genuine user click so Shopify's
     invisible CAPTCHA passes — the response loads into a hidden iframe so the
     quiz stays open. A raw fetch to /contact is rejected ("Missing CAPTCHA
     token"), which is what silently lost every email before. No external
     service, no keys, free. Wiring lives in the modal (park/slot/bind below). */

  /* Thigh caution — slim/skinny bottoms fail on the thigh even when waist+hip
     match; relaxed cuts need no note. Verbatim from Hydrogen. */
  var THIGH_SLIM = /\b(skinny|slim|jegging|cigarette|pencil|skinny[- ]?fit)\b/i;
  function thighNote(t) { return t && THIGH_SLIM.test(t) ? 'Runs slim through the thigh. Size up if you’re athletic there.' : null; }

  /* ---- expose the engine for tests/other scripts ---- */
  window.FitFinderEngine = { parseSizeChart: parseSizeChart, recommendSize: recommendSize, estimateBody: estimateBody, detectStretchIn: detectStretchIn, standardRowsForSizes: standardRowsForSizes, stdRowForLabel: stdRowForLabel };

  /* ---- UI ---- */
  var bound = new WeakSet();

  function init(root) {
    (root || document).querySelectorAll('[data-fit-finder]').forEach(function (el) {
      if (bound.has(el)) return;
      bound.add(el);
      setup(el);
    });
  }

  function setup(el) {
    var productText = el.getAttribute('data-fit-text') || '';
    var hasSize = !!el.getAttribute('data-fit-size-option');

    // Recommend against the standard BODY chart, mapped to THIS product's own
    // size labels (see standardRowsForSizes). The product's garment table is no
    // longer used for matching — only for the size table shown elsewhere.
    var sizeValues = (el.getAttribute('data-fit-sizes') || '').split('~~').map(function (s) { return s.trim(); }).filter(Boolean);
    var rows = standardRowsForSizes(sizeValues);

    // Availability: need a size option and at least two mappable sizes, or the
    // whole thing hides (better no recommendation than a wrong one on an unusual
    // size system it cannot map).
    if (!rows || !hasSize) { el.hidden = true; return; }
    el.hidden = false;

    var stretchIn = detectStretchIn(productText);
    var chartKind = 'body';
    var opts = { stretchIn: stretchIn, chartKind: chartKind, shapes: {}, thigh: thighNote(el.getAttribute('data-fit-title') || '') };
    try { opts.shapes = JSON.parse(el.getAttribute('data-fit-shapes') || '{}'); } catch (e) { opts.shapes = {}; }

    var link = el.querySelector('[data-fit-link]');
    var openBtn = el.querySelector('[data-fit-open]');
    var accent = '#c04a63';

    function currentResult() {
      var p = loadProfile();
      if (!p) return null;
      return recommendSize(rows, p, opts);
    }

    function refreshLabel() {
      var r = currentResult();
      if (link) link.textContent = r ? ('Your size is ' + r.best.label) : "What's my size?";
      // auto-apply
      var p = loadProfile();
      if (p && p.autoApply && r) autoSelect(r.best.label);
    }

    function autoSelect(label) {
      if (el._applied) return;
      var norm = function (s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); };
      var target = norm(label);
      // size chips on the PDP
      var chips = document.querySelectorAll('.variant-option__value[data-option-value]');
      for (var i = 0; i < chips.length; i++) {
        var c = chips[i];
        if (norm(c.getAttribute('data-option-value')) === target) {
          if (c.getAttribute('aria-current') === 'true') { el._applied = true; return; }
          if (!c.classList.contains('is-unavailable')) { el._applied = true; c.click(); return; }
        }
      }
    }

    if (openBtn) openBtn.addEventListener('click', function () { openModal(rows, opts, refreshLabel); });
    document.addEventListener(PROFILE_EVENT, refreshLabel);
    // secondary entry (banner elsewhere) can dispatch this
    document.addEventListener('fashorio:open-fit', function () { openModal(rows, opts, refreshLabel); });
    refreshLabel();
  }

  /* ---- modal ---- */
  var FLOW = ['measure', 'tape', 'bra', 'age', 'belly', 'hip', 'pref'];

  function openModal(rows, opts, onDone) {
    var existing = loadProfile();
    var draft = existing ? Object.assign({}, existing) : { v: 2, belly: 'average', hip: 'average', pref: 'average', autoApply: true };
    var state = { step: existing ? 'result' : 'measure', imperialH: true, imperialW: true, feet: '5', inches: '5', privacyOpen: false, editOpen: null, editSnap: null, emailOpen: false };
    // Seed the height/weight inputs from a saved profile so the edit screen shows real values.
    if (existing && existing.heightCm) {
      var totIn = existing.heightCm / 2.54;
      state.feet = String(Math.floor(totIn / 12));
      state.inches = String(Math.round(totIn - Math.floor(totIn / 12) * 12));
      state.cm = String(Math.round(existing.heightCm));
    }
    if (existing && existing.weightKg) {
      state.weight = String(Math.round(existing.weightKg / 0.4536));
    }

    var overlay = document.createElement('div');
    overlay.className = 'ff-overlay';
    overlay.innerHTML = '<div class="ff-backdrop" data-ff-close></div><div class="ff-panel" role="dialog" aria-modal="true" aria-label="Fit Finder"></div>';
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';
    var panel = overlay.querySelector('.ff-panel');

    /* Native email form (server-rendered, CAPTCHA-bound at page load) lives in
       the .fit-finder container. render() rebuilds the panel each time, so the
       form is parked back to its home before every rebuild and re-slotted after,
       which keeps the element (and its CAPTCHA binding) alive. */
    var fitRoot = (opts && opts.root) || document.querySelector('[data-fit-finder]');
    var emailForm = fitRoot ? fitRoot.querySelector('[data-ff-emailform]') : null; // the wrap div
    var newsSink = fitRoot ? fitRoot.querySelector('.ff-news-sink') : null;
    var emailBound = false, emailPending = false, emailPendingAddr = '', onEmSubmit = null, onEmLoad = null;
    function parkEmailForm() {
      if (emailForm && panel.contains(emailForm)) { emailForm.hidden = true; fitRoot.appendChild(emailForm); }
    }
    function slotEmailForm() {
      if (!emailForm) return;
      var slot = panel.querySelector('[data-ff-emailslot]');
      if (!slot) return;
      emailForm.hidden = false;
      slot.appendChild(emailForm);
      bindEmailForm();
      var em = emailForm.querySelector('input[name="contact[email]"]'); if (em) em.focus();
    }
    function bindEmailForm() {
      // Once per modal open; removed on close() so persistent form/iframe don't
      // stack listeners across reopens.
      if (emailBound || !emailForm) return; emailBound = true;
      var f = emailForm.querySelector('form') || emailForm;
      onEmSubmit = function () {
        // Real user submit: let it post natively to the iframe so Shopify's
        // CAPTCHA runs. Just mark it pending and show a busy button.
        var em = f.querySelector('input[name="contact[email]"]');
        emailPendingAddr = em ? (em.value || '').trim() : '';
        emailPending = true;
        var btn = f.querySelector('[data-ff-newsbtn]'); if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
        var err = f.querySelector('[data-ff-newserr]'); if (err) err.hidden = true;
      };
      onEmLoad = function () {
        if (!emailPending) return; // ignore the initial blank iframe load
        emailPending = false;
        // The post reached Shopify (iframe loaded) and Shopify created the
        // subscriber. The response often redirects cross-origin (challenge /
        // customer_posted), so its URL can't be read reliably — treat a
        // completed submit of a client-validated email as success. Only flag an
        // error if we can positively read a Shopify form error back.
        var ok = true;
        try {
          var doc = newsSink.contentDocument;
          if (doc && /can't be blank|is invalid|already/i.test(doc.body.innerText || '')) ok = false;
        } catch (e) { /* cross-origin: leave ok = true */ }
        var f2 = emailForm.querySelector('form') || emailForm;
        if (ok) {
          draft.email = emailPendingAddr; draft.v = 2; saveProfile(draft);
          render(); // parks the form + shows the "Saved" state
        } else {
          var btn = f2.querySelector('[data-ff-newsbtn]'); if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
          var err = f2.querySelector('[data-ff-newserr]'); if (err) err.hidden = false;
        }
      };
      f.addEventListener('submit', onEmSubmit);
      if (newsSink) newsSink.addEventListener('load', onEmLoad);
    }

    function close() {
      // Auto-apply: if the shopper opted in, closing (X, backdrop or Escape)
      // applies the recommended size to the PDP, so closing behaves the same as
      // the "Select size" button. If auto-apply is off, closing just closes.
      if (draft && draft.autoApply && draft.heightCm) {
        try { var _res = recommendSize(rows, draft, opts); if (_res && _res.best) selectSize(_res.best.label); } catch (e) {}
      }
      parkEmailForm(); // rescue the native form before the overlay (its slot) is removed
      if (emailForm && onEmSubmit) { var ef = emailForm.querySelector('form') || emailForm; ef.removeEventListener('submit', onEmSubmit); }
      if (newsSink && onEmLoad) newsSink.removeEventListener('load', onEmLoad);
      document.documentElement.style.overflow = '';
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', function (e) { if (e.target.closest('[data-ff-close]')) close(); });

    function heightCm() { return state.imperialH ? (parseFloat(state.feet) || 0) * 30.48 + (parseFloat(state.inches) || 0) * 2.54 : parseFloat(state.cm) || 0; }
    function weightKg() { return state.imperialW ? (parseFloat(state.weight) || 0) * 0.4536 : parseFloat(state.weight) || 0; }
    function measureValid() { var h = heightCm(), w = weightKg(); return h >= 100 && h <= 230 && w >= 25 && w <= 300; }

    function stepIndex() { return FLOW.indexOf(state.step); }

    var STEP_LABELS = { measure: 'Measurements', tape: 'Measurements', bra: 'Bra size', age: 'Age', belly: 'Belly shape', hip: 'Hip shape', pref: 'Fit preference', result: 'Your best fit' };

    function shieldIcon() { return '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.63 2.1a1 1 0 0 1 .74 0l7 2.8A1 1 0 0 1 20 5.83V11c0 5.02-3.32 9.19-7.68 10.94a1 1 0 0 1-.74 0C7.22 20.19 4 16.02 4 11V5.83a1 1 0 0 1 .63-.93l7-2.8Zm4.5 6.62a1 1 0 0 1 .04 1.41l-4.6 4.8a1 1 0 0 1-1.43.02l-2.4-2.36a1 1 0 1 1 1.4-1.43l1.68 1.65 3.9-4.07a1 1 0 0 1 1.41-.02Z"/></svg>'; }
    function privacyNote() {
      return '<div class="ff-privacynote">' +
        '<span class="ff-privacynote__icon" aria-hidden="true">' + shieldIcon() + '</span>' +
        '<p class="ff-privacynote__text">Fit Finder helps you choose the right size and reduces returns. The measurements you provide are stored on your device. We use them only to compare against the measurements published for each product. We do not sell this information, share it with advertisers, or send it to any third-party fit service. You can delete your profile at any time from this window. <a href="/policies/privacy-policy" target="_blank" rel="noopener">Learn more</a></p>' +
        '<button type="button" class="ff-privacynote__x" data-privacy aria-label="Dismiss">' + xIcon() + '</button>' +
        '</div>';
    }
    function header() {
      var idx = stepIndex();
      var showProg = idx >= 0;
      var segs = '';
      for (var i = 0; i < FLOW.length; i++) segs += '<span class="ff-seg' + (i <= idx ? ' is-on' : '') + '"></span>';
      var label = (STEP_LABELS[state.step] || '').toUpperCase();
      var cap = showProg ? ('Step ' + (idx + 1) + ' of ' + FLOW.length + (label ? ' · ' + label : '')) : '';
      return '<div class="ff-head">' +
        '<span class="ff-brand"><span class="ff-badge">' + tapeIcon() + '</span> FIT FINDER</span>' +
        '<span class="ff-head-right">' +
        '<button type="button" class="ff-privacy" data-privacy aria-expanded="' + (state.privacyOpen ? 'true' : 'false') + '">Privacy</button>' +
        '<button type="button" class="ff-x" data-ff-close aria-label="Close">' + xIcon() + '</button>' +
        '</span>' +
        '</div>' +
        (state.privacyOpen ? privacyNote() : '') +
        (showProg ? '<div class="ff-prog">' + segs + '</div><p class="ff-progcap">' + cap + '</p>' : '');
    }

    function render() {
      var body = '';
      switch (state.step) {
        case 'measure': body = stepMeasure(); break;
        case 'tape': body = stepTape(); break;
        case 'bra': body = stepBra(); break;
        case 'age': body = stepAge(); break;
        case 'belly': body = stepShape('belly', 'Your belly shape', [['flatter', 'Flatter'], ['average', 'Average'], ['curvier', 'Curvier']]); break;
        case 'hip': body = stepShape('hip', 'Your hip shape', [['straighter', 'Straighter'], ['average', 'Average'], ['wider', 'Wider']]); break;
        case 'pref': body = stepPref(); break;
        case 'result': body = stepResult(); break;
        case 'edit': body = stepEdit(); break;
      }
      parkEmailForm(); // rescue the native form before the panel is wiped
      panel.innerHTML = header() + '<div class="ff-body">' + body + '</div>';
      wire();
      slotEmailForm(); // re-insert it into the result card if that step is showing
    }

    function next() { var i = stepIndex(); if (i >= 0 && i < FLOW.length - 1) { state.step = FLOW[i + 1]; render(); } }
    function back() { var i = stepIndex(); if (i > 0) { state.step = FLOW[i - 1]; render(); } }

    /* --- steps --- */
    function unitToggle(group, impLabel, metLabel, isImp) {
      return '<div class="ff-unit-toggle" role="group">' +
        '<button type="button" data-setunit="' + group + '-imp" class="' + (isImp ? 'is-on' : '') + '">' + impLabel + '</button>' +
        '<button type="button" data-setunit="' + group + '-met" class="' + (isImp ? '' : 'is-on') + '">' + metLabel + '</button>' +
        '</div>';
    }

    function iconArrow() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 19l-7-7 7-7"/></svg>'; }

    /* The per-step shell, matching the Hydrogen storefront's StepShell: a back
       arrow (when there is a previous step), the centred title, and a Skip link
       (when the step is optional), then the centred sub, then the content. */
    function shell(opts, content) {
      var backBtn = opts.back ? '<button type="button" class="ff-shback" data-back aria-label="Back">' + iconArrow() + '</button>' : '<span class="ff-shspace"></span>';
      var skipBtn = opts.skip ? '<button type="button" class="ff-shskip" data-skip="' + opts.skip + '">Skip</button>' : '<span class="ff-shspace"></span>';
      return '<div class="ff-shelltop">' + backBtn + '<h2 class="ff-title">' + opts.title + '</h2>' + skipBtn + '</div>' +
        (opts.sub ? '<p class="ff-sub">' + opts.sub + '</p>' : '') + content;
    }

    /* Wrong-unit guards, verbatim from Hydrogen. */
    function unitSuspicion() {
      if (!measureValid()) return null;
      var hM = heightCm() / 100; if (!hM) return null;
      var bmi = weightKg() / (hM * hM);
      return bmi < 14 ? 'low' : bmi > 55 ? 'high' : null;
    }
    function tapeValid(m, v) { var l = TAPE_LIMITS[m]; return v >= l[0] && v <= l[1]; }

    function howToMeasure() {
      return '<details class="ff-howto"><summary>How to measure &rarr;</summary>' +
        '<ul>' +
        '<li><strong>Bust</strong>: around the fullest part, tape level all the way round and straight across your back.</li>' +
        '<li><strong>Waist</strong>: the narrowest part, usually just above the navel. Bend to one side; where it creases is your natural waist.</li>' +
        '<li><strong>Hip</strong>: the fullest part, roughly 7 to 9″ below the waist, feet together.</li>' +
        '<li>Measure over light clothing or bare skin, keep the tape snug but never pulled tight, and breathe normally. Do not hold your breath in.</li>' +
        '</ul></details>';
    }

    function stepMeasure() {
      var h = state.imperialH
        ? '<label class="ff-in"><input type="number" data-k="feet" value="' + (state.feet || '') + '" inputmode="decimal"><span>ft</span></label><label class="ff-in"><input type="number" data-k="inches" value="' + (state.inches || '') + '" inputmode="decimal"><span>in</span></label>'
        : '<label class="ff-in"><input type="number" data-k="cm" value="' + (state.cm || '') + '" inputmode="decimal"><span>cm</span></label>';
      var w = '<label class="ff-in"><input type="number" data-k="weight" value="' + (state.weight || '') + '" inputmode="decimal"><span>' + (state.imperialW ? 'lbs' : 'kg') + '</span></label>';
      var susp = unitSuspicion();
      var suspMsg = susp ? '<p class="ff-warn">' + (susp === 'low' ? 'That’s unusually light for this height. Did you mean ' + (state.weight || '') + ' kg? Tap the unit toggle to switch.' : 'That’s unusually heavy for this height. Check the unit toggle.') + '</p>' : '';
      return shell({ title: 'Your measurements', sub: 'Kept on this device and used only to size you here. Never sold or shared.' },
        '<div class="ff-field"><div class="ff-flabel">Height</div><div class="ff-row">' + h + unitToggle('H', 'ft/in', 'cm', state.imperialH) + '</div></div>' +
        '<div class="ff-field"><div class="ff-flabel">Weight</div><div class="ff-row">' + w + unitToggle('W', 'lbs', 'kg', state.imperialW) + '</div></div>' +
        suspMsg +
        '<div class="ff-nav ff-nav--first"><button type="button" class="ff-skip" data-ff-skipclose>Skip</button>' + primary('Continue', 'go-measure', !measureValid()) + '</div>');
    }
    function stepTape() {
      return shell({ title: 'Know your measurements?', sub: 'Optional, but this is the real thing. Size charts are built from bust, waist and hip, so a tape lets us match your numbers to the chart with no estimating at all.', back: true, skip: 'go-tape' },
        '<div class="ff-row3">' + tapeIn('Bust', 'bustIn') + tapeIn('Waist', 'waistIn') + tapeIn('Hip', 'hipIn') + '</div>' +
        howToMeasure() +
        '<p class="ff-foot">Fill in what you know. Even one number still helps. Anything you leave blank we estimate from your height and weight, and the result labels it “est.” so you always know which is which.</p>' +
        primary('Continue', 'go-tape'));
    }
    function tapeIn(label, key) {
      var m = key === 'bustIn' ? 'bust' : key === 'waistIn' ? 'waist' : 'hip';
      var val = draft[key];
      var bad = val !== undefined && val !== '' && !tapeValid(m, val);
      var warn = bad ? '<span class="ff-warn ff-warn--sm">' + TAPE_LIMITS[m][0] + ' to ' + TAPE_LIMITS[m][1] + '″ expected. Is that centimetres?</span>' : '';
      return '<div class="ff-tape"><span class="ff-inlab">' + label + '</span><label class="ff-in"><input type="number" data-k="' + key + '" value="' + (val || '') + '" inputmode="decimal"><span>in</span></label>' + warn + '</div>';
    }
    function stepBra() {
      var bands = [30, 32, 34, 36, 38, 40, 42, 44, 46, 48], cups = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G'];
      var bandBtns = bands.map(function (b) { return '<button type="button" class="ff-choice' + (draft.braBand === b ? ' is-on' : '') + '" data-band="' + b + '">' + b + '</button>'; }).join('');
      var cupBtns = cups.map(function (c) { return '<button type="button" class="ff-choice' + (draft.braCup === c ? ' is-on' : '') + '" data-cup="' + c + '">' + c + '</button>'; }).join('');
      return shell({ title: 'Bra size', sub: 'Optional. A bra size gives an exact bust measurement, the single biggest accuracy boost.', back: true, skip: 'go-bra' },
        '<div class="ff-field"><div class="ff-flabel">Band</div><div class="ff-grid5">' + bandBtns + '</div></div>' +
        '<div class="ff-field"><div class="ff-flabel">Cup</div><div class="ff-grid4">' + cupBtns + '</div></div>' +
        primary('Continue', 'go-bra', !(draft.braBand && draft.braCup)));
    }
    function stepAge() {
      var ageOk = draft.age >= 14 && draft.age <= 100;
      return shell({ title: 'How old are you?', sub: 'Optional. Age changes how weight distributes, which sharpens the waist estimate.', back: true, skip: 'go-age' },
        '<div class="ff-agewrap"><label class="ff-in"><input type="number" data-k="age" value="' + (draft.age || '') + '" inputmode="numeric"><span>years</span></label>' +
        primary('Continue', 'go-age', !ageOk) + '</div>');
    }
    function stepShape(key, title, opts2) {
      var cards = opts2.map(function (o) {
        var url = (opts.shapes && opts.shapes[key + ':' + o[0]]) || '';
        var pic = url ? '<img class="ff-shapeimg" src="' + url + '" alt="" loading="lazy">' : '';
        return '<button type="button" class="ff-shape' + (draft[key] === o[0] ? ' is-on' : '') + '" data-shape="' + key + '" data-val="' + o[0] + '">' + pic + '<span class="ff-shapelab">' + o[1] + '</span></button>';
      }).join('');
      return shell({ title: title, sub: 'Pick the closest match:', back: true },
        '<div class="ff-grid3">' + cards + '</div>');
    }
    var PREF_STEPS = ['much-tighter', 'tighter', 'average', 'looser', 'much-looser'];
    var PREF_LABELS = ['Much tighter', 'Tighter', 'Average', 'Looser', 'Much looser'];
    /* Lulus-style preference slider: tooltip label above the thumb with a caret,
       a filled rose track, tick marks and « Tighter / Looser » ends. */
    function prefSlider() {
      var idx = PREF_STEPS.indexOf(draft.pref); if (idx < 0) idx = 2;
      var pct = idx / (PREF_STEPS.length - 1) * 100;
      var ticks = PREF_STEPS.map(function () { return '<span></span>'; }).join('');
      return '<div class="ff-pref">' +
        '<div class="ff-pref__stage">' +
        '<span class="ff-pref__tip" data-preftip style="left:' + pct + '%">' + PREF_LABELS[idx] + '</span>' +
        '<span class="ff-pref__track"><span class="ff-pref__fill" data-preffill style="width:' + pct + '%"></span></span>' +
        '<span class="ff-pref__ticks">' + ticks + '</span>' +
        '<input type="range" class="ff-slider" min="0" max="4" step="1" value="' + idx + '" data-pref aria-label="Fit preference">' +
        '</div>' +
        '<div class="ff-prefends"><span>« Tighter</span><span>Looser »</span></div>' +
        '</div>';
    }
    function stepPref() {
      return shell({ title: 'Fit preference', sub: 'I want this item to fit…', back: true },
        prefSlider() + primary('See my size', 'finish'));
    }

    /* --- edit profile: the "Your fit profile" accordion (Hydrogen parity) ---
       Rows and order match Hydrogen FIELD_ROWS exactly: Measurements (tape) first,
       then Height, Weight, shapes, Bra, Age, Fit preference. */
    var EDIT_ROWS = [
      ['tape', 'Measurements'],
      ['height', 'Height'],
      ['weight', 'Weight'],
      ['belly', 'Belly shape'],
      ['hip', 'Hip shape'],
      ['bra', 'Bra size'],
      ['age', 'Age'],
      ['pref', 'Fit preference']
    ];
    function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
    function editValue(key) {
      if (key === 'height') {
        if (!draft.heightCm) return 'Not set';
        return state.imperialH ? (state.feet || '?') + 'ft ' + (state.inches || '0') + 'in' : Math.round(draft.heightCm) + ' cm';
      }
      if (key === 'weight') return state.weight ? state.weight + ' ' + (state.imperialW ? 'lbs' : 'kg') : 'Not set';
      if (key === 'tape') {
        var p = [];
        if (draft.bustIn) p.push('B ' + draft.bustIn + '″');
        if (draft.waistIn) p.push('W ' + draft.waistIn + '″');
        if (draft.hipIn) p.push('H ' + draft.hipIn + '″');
        return p.length ? p.join(' · ') : 'Estimated';
      }
      if (key === 'bra') return draft.braBand && draft.braCup ? draft.braBand + draft.braCup : 'Not set';
      if (key === 'belly') return cap(draft.belly) || 'Not set';
      if (key === 'hip') return cap(draft.hip) || 'Not set';
      if (key === 'age') return draft.age ? draft.age + ' yrs' : 'Not set';
      if (key === 'pref') return PREF_LABELS[PREF_STEPS.indexOf(draft.pref)] || 'Average';
      return '';
    }
    function editShapeGrid(key, opts2) {
      var cards = opts2.map(function (o) {
        var url = (opts.shapes && opts.shapes[key + ':' + o[0]]) || '';
        var pic = url ? '<img class="ff-shapeimg" src="' + url + '" alt="" loading="lazy">' : '';
        return '<button type="button" class="ff-shape' + (draft[key] === o[0] ? ' is-on' : '') + '" data-eshape="' + key + '" data-eval="' + o[0] + '">' + pic + '<span class="ff-shapelab">' + o[1] + '</span></button>';
      }).join('');
      return '<div class="ff-grid3">' + cards + '</div>';
    }
    function editEditor(key) {
      if (key === 'height') {
        var h = state.imperialH
          ? '<label class="ff-in"><input type="number" data-k="feet" value="' + (state.feet || '') + '" inputmode="decimal"><span>ft</span></label><label class="ff-in"><input type="number" data-k="inches" value="' + (state.inches || '') + '" inputmode="decimal"><span>in</span></label>'
          : '<label class="ff-in"><input type="number" data-k="cm" value="' + (state.cm || '') + '" inputmode="decimal"><span>cm</span></label>';
        return '<div class="ff-row">' + h + unitToggle('H', 'ft/in', 'cm', state.imperialH) + '</div>';
      }
      if (key === 'weight') {
        var w = '<label class="ff-in"><input type="number" data-k="weight" value="' + (state.weight || '') + '" inputmode="decimal"><span>' + (state.imperialW ? 'lbs' : 'kg') + '</span></label>';
        return '<div class="ff-row">' + w + unitToggle('W', 'lbs', 'kg', state.imperialW) + '</div>';
      }
      if (key === 'tape') {
        var clear = (draft.bustIn || draft.waistIn || draft.hipIn) ? '<button type="button" class="ff-clearlink" data-cleartape>Clear measurements</button>' : '';
        return '<div class="ff-row3">' + tapeIn('Bust', 'bustIn') + tapeIn('Waist', 'waistIn') + tapeIn('Hip', 'hipIn') + '</div>' +
          '<p class="ff-foot">Measured numbers beat any estimate. Anything left blank we work out from your height and weight and label “est.”.</p>' + clear;
      }
      if (key === 'bra') {
        var bands = [30, 32, 34, 36, 38, 40, 42, 44, 46, 48], cups = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G'];
        var bb = bands.map(function (b) { return '<button type="button" class="ff-choice' + (draft.braBand === b ? ' is-on' : '') + '" data-band="' + b + '">' + b + '</button>'; }).join('');
        var cb = cups.map(function (c) { return '<button type="button" class="ff-choice' + (draft.braCup === c ? ' is-on' : '') + '" data-cup="' + c + '">' + c + '</button>'; }).join('');
        var rm = (draft.braBand || draft.braCup) ? '<button type="button" class="ff-clearlink" data-removebra>Remove bra size</button>' : '';
        return '<div class="ff-field"><div class="ff-flabel">Band</div><div class="ff-grid5">' + bb + '</div></div><div class="ff-field"><div class="ff-flabel">Cup</div><div class="ff-grid4">' + cb + '</div></div>' + rm;
      }
      if (key === 'belly') return editShapeGrid('belly', [['flatter', 'Flatter'], ['average', 'Average'], ['curvier', 'Curvier']]);
      if (key === 'hip') return editShapeGrid('hip', [['straighter', 'Straighter'], ['average', 'Average'], ['wider', 'Wider']]);
      if (key === 'age') return '<label class="ff-in"><input type="number" data-k="age" value="' + (draft.age || '') + '" inputmode="numeric"><span>years</span></label>';
      if (key === 'pref') return '<p class="ff-prefsub">I want this item to fit…</p>' + prefSlider();
      return '';
    }
    function stepEdit() {
      var rowsHtml = EDIT_ROWS.map(function (r) {
        var key = r[0], open = state.editOpen === key;
        var head = '<button type="button" class="ff-erow__head" data-eopen="' + key + '" aria-expanded="' + (open ? 'true' : 'false') + '">' +
          '<span class="ff-erow__lab">' + r[1] + '</span><span class="ff-erow__val">' + (open ? 'Editing…' : editValue(key)) + '</span></button>';
        var isHW = key === 'height' || key === 'weight';
        var blocked = isHW && !measureValid();
        var susp = isHW && !blocked ? unitSuspicion() : null;
        var warn = blocked ? '<p class="ff-warn">Enter a valid height and weight to save.</p>'
          : susp ? '<p class="ff-warn">' + (susp === 'low' ? 'That’s unusually light for this height. Did you mean ' + (state.weight || '') + ' kg?' : 'That’s unusually heavy for this height. Check the unit toggle.') + '</p>' : '';
        var body = open ? '<div class="ff-erow__body">' + editEditor(key) + warn +
          '<div class="ff-esave">' + primary('Save changes', 'esave', blocked) + '<button type="button" class="ff-back" data-ecancel>Cancel</button></div></div>' : '';
        return '<div class="ff-erow">' + head + body + '</div>';
      }).join('');
      return shell({ title: 'Your fit profile', sub: 'Update anything and we will recalculate your size.', back: true },
        '<div class="ff-edit">' + rowsHtml + '</div>' +
        primary('Done', 'edit-done'));
    }
    function resultCard(size, isBest) {
      var chips = size.details.map(function (d) {
        var v = d.verdict === 'fits' ? 'fits' : d.verdict;
        return '<span class="ff-rchip"><span class="ff-dot ff-dot--' + d.verdict + '"></span>' + d.measure.toLowerCase() + ' ' + v + '</span>';
      }).join('');
      return '<div class="ff-rcard' + (isBest ? ' is-best' : '') + '">' +
        '<div class="ff-rtop"><span class="ff-rlabel">' + size.label + '</span>' +
        '<span class="ff-rchips">' + chips + (isBest ? '<span class="ff-rcheck">' + checkIcon() + '</span>' : '') + '</span></div>' +
        '<div class="ff-rbarwrap"><div class="ff-rbar' + (isBest ? ' is-best' : '') + '" style="width:' + size.score + '%">' + size.score + '%</div></div>' +
        '</div>';
    }
    function resultSub(res) {
      var used = {}; res.best.details.forEach(function (d) { used[d.measure] = true; });
      var parts = [];
      function lab(real, name, v) { return (real ? '' : 'est. ') + name + ' ' + Math.round(v) + '″'; }
      if (used.Bust) parts.push(lab(res.measured.bust, 'bust', res.bustIn));
      if (used.Waist) parts.push(lab(res.measured.waist, 'waist', res.waistIn));
      if (used.Hip) parts.push(lab(res.measured.hip, 'hip', res.hipIn));
      var lead = parts.length ? parts.join(' · ') : 'your measurements';
      return lead.charAt(0).toUpperCase() + lead.slice(1) + ', matched against this style’s size chart.';
    }
    function howWePicked(res) {
      var txt;
      if (res.chartKind === 'body') txt = 'This style publishes <strong>the body measurements each size fits</strong>, so we compare your numbers to them directly, allowing for how much the fabric gives.';
      else if (res.chartKind === 'garment') txt = 'This style publishes <strong>the garment’s own flat measurements</strong> (the dress, not the body), so we match your numbers against them and factor in the fabric’s stretch. Treat it as a close guide rather than an exact body match.';
      else txt = 'We compare your numbers against the bust, waist and hip published on <strong>this style’s own size chart</strong>, allowing for how much the fabric gives.';
      return '<div class="ff-picked">' +
        '<p class="ff-picked__t">How we picked ' + res.best.label + '</p>' +
        '<p class="ff-picked__b">' + txt + ' Either way it’s arithmetic on real measurements, not a guess from other shoppers’ orders. It is a strong starting point, not a guarantee.</p>' +
        '<p class="ff-picked__priv"><span class="ff-picked__shield" aria-hidden="true">' + shieldIcon() + '</span><span>Your measurements are stored on your device. They are never sold or shared. <a href="/policies/privacy-policy" target="_blank" rel="noopener">Privacy</a></span></p>' +
        '</div>';
    }
    /* Save prompt: a single button that, on click, reveals the email field in
       place. Submitting saves the profile and adds the email to marketing. */
    function emailCapture() {
      if (draft.email) return '<div class="ff-email is-done"><span class="ff-email__ok" aria-hidden="true">' + checkIcon() + '</span><p class="ff-emailok">Saved! Here is <strong>10% off</strong> your first order. Use code <strong class="ff-code">FARIO10</strong> at checkout.</p></div>';
      if (!state.emailOpen) {
        return '<button type="button" class="ff-savebtn" data-emailstart>' + shieldIcon2() + 'Save my size &amp; get 10% off</button>';
      }
      // Slot the real server-rendered {% form 'customer' %} gets moved into
      // (slotEmailForm, called from render). Keeping the form server-rendered is
      // what lets Shopify's CAPTCHA pass on the real Save click.
      return '<div class="ff-email ff-email--slot" data-ff-emailslot></div>';
    }
    function shieldIcon2() { return '<span class="ff-savebtn__i" aria-hidden="true">' + shieldIcon() + '</span>'; }
    function stepResult() {
      var res = recommendSize(rows, draft, opts);
      if (!res) return shell({ title: 'Your best fit' }, '<p class="ff-sub">We could not read this size chart.</p>');
      var b = res.best;
      var runner = res.runnerUp && res.runnerUp.score > 0 ? resultCard(res.runnerUp, false) : '';
      var lengthNote = res.length && res.length.verdict !== 'regular'
        ? '<p class="ff-note ff-note--amber">Length: inseam runs ' + res.length.garmentIn + '″, ' + (res.length.verdict === 'long' ? 'longer than your ~' + res.length.bodyIn + '″, so plan to cuff or hem.' : 'shorter than your ~' + res.length.bodyIn + '″, so these will sit cropped.') + '</p>'
        : '';
      var between = res.betweenSizes ? '<p class="ff-note">You’re between ' + res.betweenSizes.smaller + ' and ' + res.betweenSizes.larger + '. Size up to ' + res.betweenSizes.larger + ' for a relaxed fit, or down to ' + res.betweenSizes.smaller + ' for a fitted look.</p>' : '';
      var thighN = opts.thigh ? '<p class="ff-note">' + opts.thigh + '</p>' : '';
      var toggle = '<div class="ff-autoapply"><span class="ff-autoapply__txt"><span class="ff-autoapply__t">Auto-apply my size</span><span class="ff-autoapply__s">Pre-select the best size on every product</span></span>' +
        '<button type="button" role="switch" aria-checked="' + (draft.autoApply ? 'true' : 'false') + '" class="ff-switch2' + (draft.autoApply ? ' is-on' : '') + '" data-autoapply><span class="ff-switch2__knob"></span></button></div>';
      return shell({ title: 'Your best fit', sub: resultSub(res) },
        resultCard(b, true) + runner + lengthNote + between + thighN + toggle + emailCapture() + howWePicked(res) +
        primary('Select size ' + b.label, 'apply') +
        '<div class="ff-resfoot"><button type="button" class="ff-link" data-edit>Edit my info</button><button type="button" class="ff-link" data-delete>Delete profile</button></div>');
    }

    function primary(text, action, disabled) {
      return '<button type="button" class="ff-primary" data-action="' + action + '"' + (disabled ? ' disabled' : '') + '>' + text + '</button>';
    }
    function nav(action, disabled) {
      return '<div class="ff-nav"><button type="button" class="ff-back" data-back>Back</button><button type="button" class="ff-skip" data-skip="' + action + '">Skip</button>' + primary('Continue', action, disabled) + '</div>';
    }

    function commitMeasure() { draft.heightCm = Math.round(heightCm()); draft.weightKg = Math.round(weightKg() * 10) / 10; }

    function wire() {
      panel.querySelectorAll('[data-k]').forEach(function (inp) {
        inp.addEventListener('input', function () {
          var k = inp.getAttribute('data-k');
          if (k === 'feet' || k === 'inches' || k === 'cm' || k === 'weight') { state[k] = inp.value; }
          else if (k === 'age') { draft.age = parseInt(inp.value, 10) || undefined; }
          else { draft[k] = parseFloat(inp.value) || undefined; }
          // live-enable the Continue button
          var pb = panel.querySelector('.ff-primary');
          if (pb) {
            if (state.step === 'measure') pb.disabled = !measureValid();
            else if (state.step === 'age') pb.disabled = !(draft.age >= 14 && draft.age <= 100);
            else if (state.step === 'edit' && (state.editOpen === 'height' || state.editOpen === 'weight')) pb.disabled = !measureValid();
          }
          // tape: show/hide the "expected range" warning inline, no re-render so focus holds
          if (k === 'bustIn' || k === 'waistIn' || k === 'hipIn') {
            var mm = k === 'bustIn' ? 'bust' : k === 'waistIn' ? 'waist' : 'hip';
            var tp = inp.closest('.ff-tape');
            if (tp) {
              var ex = tp.querySelector('.ff-warn--sm');
              var bad = inp.value.trim() !== '' && !tapeValid(mm, parseFloat(inp.value));
              if (bad && !ex) { var s = document.createElement('span'); s.className = 'ff-warn ff-warn--sm'; s.textContent = TAPE_LIMITS[mm][0] + ' to ' + TAPE_LIMITS[mm][1] + '″ expected. Is that centimetres?'; tp.appendChild(s); }
              else if (!bad && ex) { ex.remove(); }
            }
          }
        });
        // measure: re-render on blur so the wrong-unit note can appear
        if (['feet', 'inches', 'cm', 'weight'].indexOf(inp.getAttribute('data-k')) !== -1) {
          inp.addEventListener('change', function () { if (state.step === 'measure' || (state.step === 'edit' && (state.editOpen === 'height' || state.editOpen === 'weight'))) render(); });
        }
      });
      panel.querySelectorAll('[data-setunit]').forEach(function (t) {
        t.addEventListener('click', function () {
          var v = t.getAttribute('data-setunit');
          if (v === 'H-imp') state.imperialH = true;
          else if (v === 'H-met') state.imperialH = false;
          else if (v === 'W-imp') state.imperialW = true;
          else if (v === 'W-met') state.imperialW = false;
          render();
        });
      });
      panel.querySelectorAll('[data-band]').forEach(function (b) { b.addEventListener('click', function () { draft.braBand = parseInt(b.getAttribute('data-band'), 10); render(); }); });
      panel.querySelectorAll('[data-cup]').forEach(function (b) { b.addEventListener('click', function () { draft.braCup = b.getAttribute('data-cup'); render(); }); });
      panel.querySelectorAll('[data-shape]').forEach(function (s) { s.addEventListener('click', function () { draft[s.getAttribute('data-shape')] = s.getAttribute('data-val'); next(); }); });
      var slider = panel.querySelector('[data-pref]');
      if (slider) slider.addEventListener('input', function () {
        var i = parseInt(slider.value, 10);
        draft.pref = PREF_STEPS[i];
        var pct = i / (PREF_STEPS.length - 1) * 100;
        var tip = panel.querySelector('[data-preftip]'); if (tip) { tip.textContent = PREF_LABELS[i]; tip.style.left = pct + '%'; }
        var fill = panel.querySelector('[data-preffill]'); if (fill) fill.style.width = pct + '%';
      });
      panel.querySelectorAll('[data-privacy]').forEach(function (b) { b.addEventListener('click', function () { state.privacyOpen = !state.privacyOpen; render(); }); });
      panel.querySelectorAll('[data-back]').forEach(function (b) { b.addEventListener('click', back2); });
      panel.querySelectorAll('[data-skip]').forEach(function (s) { s.addEventListener('click', function () { onSkip(s.getAttribute('data-skip')); }); });
      var skc = panel.querySelector('[data-ff-skipclose]'); if (skc) skc.addEventListener('click', function () { close(); if (onDone) onDone(); });
      var aa = panel.querySelector('[data-autoapply]'); if (aa) aa.addEventListener('click', function () { draft.autoApply = !draft.autoApply; saveProfile(draft); aa.classList.toggle('is-on', draft.autoApply); aa.setAttribute('aria-checked', draft.autoApply ? 'true' : 'false'); });
      var ed = panel.querySelector('[data-edit]'); if (ed) ed.addEventListener('click', function () { state.editOpen = null; state.step = 'edit'; render(); });
      var del = panel.querySelector('[data-delete]'); if (del) del.addEventListener('click', function () { clearProfile(); close(); if (onDone) onDone(); });
      // email capture — button reveals the field in place, then submit saves
      var emStart = panel.querySelector('[data-emailstart]'); if (emStart) emStart.addEventListener('click', function () { state.emailOpen = true; render(); });
      // edit accordion
      panel.querySelectorAll('[data-eopen]').forEach(function (b) {
        b.addEventListener('click', function () {
          var k = b.getAttribute('data-eopen');
          if (state.editOpen === k) { state.editOpen = null; }
          else { state.editSnap = snapshot(); state.editOpen = k; }
          render();
        });
      });
      panel.querySelectorAll('[data-eshape]').forEach(function (s) { s.addEventListener('click', function () { draft[s.getAttribute('data-eshape')] = s.getAttribute('data-eval'); render(); }); });
      var ct = panel.querySelector('[data-cleartape]'); if (ct) ct.addEventListener('click', function () { draft.bustIn = undefined; draft.waistIn = undefined; draft.hipIn = undefined; render(); });
      var rb = panel.querySelector('[data-removebra]'); if (rb) rb.addEventListener('click', function () { draft.braBand = undefined; draft.braCup = undefined; render(); });
      var ec = panel.querySelector('[data-ecancel]'); if (ec) ec.addEventListener('click', function () { restore(state.editSnap); state.editOpen = null; render(); });
      panel.querySelectorAll('[data-action]').forEach(function (btn) { btn.addEventListener('click', function () { onAction(btn.getAttribute('data-action')); }); });
    }
    function snapshot() { return { draft: JSON.parse(JSON.stringify(draft)), feet: state.feet, inches: state.inches, cm: state.cm, weight: state.weight, imperialH: state.imperialH, imperialW: state.imperialW }; }
    function restore(s) { if (!s) return; draft = s.draft; state.feet = s.feet; state.inches = s.inches; state.cm = s.cm; state.weight = s.weight; state.imperialH = s.imperialH; state.imperialW = s.imperialW; }
    function back2() {
      if (state.step === 'edit') { state.step = 'result'; render(); return; }
      if (state.step === 'result') { state.step = 'pref'; render(); return; }
      back();
    }

    function onSkip(action) {
      if (action === 'go-tape') { draft.bustIn = undefined; draft.waistIn = undefined; draft.hipIn = undefined; }
      if (action === 'go-bra') { draft.braBand = undefined; draft.braCup = undefined; }
      if (action === 'go-age') { draft.age = undefined; }
      next();
    }
    function onAction(action) {
      if (action === 'go-measure') { if (!measureValid()) return; commitMeasure(); next(); }
      else if (action === 'go-tape' || action === 'go-bra' || action === 'go-age') { next(); }
      else if (action === 'finish') { commitFinish(); }
      else if (action === 'apply') { commitFinish(true); }
      else if (action === 'esave') { if (state.editOpen === 'height' || state.editOpen === 'weight') { if (!measureValid()) return; commitMeasure(); } draft.v = 2; saveProfile(draft); state.editOpen = null; render(); }
      else if (action === 'edit-done') { draft.v = 2; saveProfile(draft); state.step = 'result'; render(); }
    }
    function commitFinish(applyNow) {
      draft.v = 2;
      saveProfile(draft);
      if (state.step !== 'result') { state.step = 'result'; render(); }
      if (applyNow) {
        var res = recommendSize(rows, draft, opts);
        close();
        if (onDone) onDone();
        if (res) selectSize(res.best.label);
      } else if (onDone) { onDone(); }
    }

    render();
  }

  function selectSize(label) {
    var norm = function (s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); };
    var target = norm(label);
    var chips = document.querySelectorAll('.variant-option__value[data-option-value]');
    for (var i = 0; i < chips.length; i++) {
      if (norm(chips[i].getAttribute('data-option-value')) === target && !chips[i].classList.contains('is-unavailable')) {
        chips[i].click();
        return;
      }
    }
  }

  /* ---- icons ---- */
  function tapeIcon() { return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><rect x="3" y="7" width="14" height="8" rx="1"/><path d="M6 7V5M10 7V4M14 7V5"/></svg>'; }
  function xIcon() { return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"/></svg>'; }
  function checkIcon() { return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 10.5l4 4 8-9"/></svg>'; }

  /* ---- boot ---- */
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  // A "Not sure of your size?" banner (Size & fit accordion) opens the same quiz
  // through the shared open event, wherever it sits on the page.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-fit-banner]')) {
      document.dispatchEvent(new CustomEvent('fashorio:open-fit'));
    }
  });
})();
